import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { PaymentOptionsDataType } from '../../../api/payment/get-payment-options';
import { checkAvailableAdyenAppleAndGoogleMethods } from '../payment.helpers';
import { changeState } from '../reducers';
import { PAYMENT_METHOD_KEYS } from '../../../constants/payment-method-keys';

const checkPaymentMethodAvailability = (paymentMethod: PAYMENT_METHOD_KEYS) => {
    switch (paymentMethod) {
        case PAYMENT_METHOD_KEYS.APPLE_PAY:
        case PAYMENT_METHOD_KEYS.GOOGLE_PAY:
            return null;
        default:
            return true;
    }
};

const buildPaymentMethodsAvailability = (allowedPaymentMethods: string[]): Map<PAYMENT_METHOD_KEYS, boolean | null> => {
    const availability = new Map<PAYMENT_METHOD_KEYS, boolean | null>();

    allowedPaymentMethods.forEach((paymentMethod) => {
        const method = paymentMethod as PAYMENT_METHOD_KEYS;

        availability.set(method, checkPaymentMethodAvailability(method));
    });

    return availability;
};

const getPendingDeviceCheckMethods = (
    paymentOptionsAvailability: Map<PAYMENT_METHOD_KEYS, boolean | null>
): PAYMENT_METHOD_KEYS[] =>
    Array.from(paymentOptionsAvailability.keys()).filter(
        (method) =>
            [PAYMENT_METHOD_KEYS.GOOGLE_PAY, PAYMENT_METHOD_KEYS.APPLE_PAY].includes(method) &&
            paymentOptionsAvailability.get(method) === null
    );

export default function usePaymentOptionsAvailability(paymentOptionsData: PaymentOptionsDataType | undefined) {
    const dispatch = useDispatch();
    const [paymentOptionsAvailability, setPaymentOptionsAvailability] = useState<Map<PAYMENT_METHOD_KEYS, boolean | null>>(
        new Map()
    );

    useEffect(() => {
        const allowedPaymentMethodsLength = !paymentOptionsData?.allowedPaymentMethods?.length;

        if (allowedPaymentMethodsLength) {
            return;
        }

        const availability = buildPaymentMethodsAvailability(paymentOptionsData.allowedPaymentMethods);

        setPaymentOptionsAvailability(availability);
        dispatch(changeState({ paymentOptionsAvailability: availability }));
    }, [paymentOptionsData?.allowedPaymentMethods?.length]);

    useEffect(() => {
        const methodsForCheck = getPendingDeviceCheckMethods(paymentOptionsAvailability);

        if (methodsForCheck.length) {
            (async () => {
                const googleAndAppleMethodAvailability = await checkAvailableAdyenAppleAndGoogleMethods(methodsForCheck);
                const updated = new Map(paymentOptionsAvailability);

                updated.set(PAYMENT_METHOD_KEYS.GOOGLE_PAY, googleAndAppleMethodAvailability[PAYMENT_METHOD_KEYS.GOOGLE_PAY]);
                updated.set(PAYMENT_METHOD_KEYS.APPLE_PAY, googleAndAppleMethodAvailability[PAYMENT_METHOD_KEYS.APPLE_PAY]);

                setPaymentOptionsAvailability(updated);
                dispatch(changeState({ paymentOptionsAvailability: updated }));
            })();
        }
    }, [paymentOptionsAvailability]);
}
